import { OrderItem } from '../lib/ui-kit/index.js';
import type { PurchaseHistory } from '../modules/DataTransformSchema';
import { ProductThumb } from './ProductThumb';
import { formatOrderDate } from '../utils/orderPresentation';

type OrderListProps = {
  orders: PurchaseHistory[];
  onSelectOrder: (order: PurchaseHistory) => void;
  /** Trailing skeleton rows appended while more orders stream in. */
  trailingSkeletonCount?: number;
};

export function OrderList({
  orders,
  onSelectOrder,
  trailingSkeletonCount = 0,
}: OrderListProps) {
  return (
    <div className="space-y-2.5" data-testid="order-list">
      {orders.map((order, index) => (
        <OrderItem
          key={`${order.order_id ?? 'item'}_${index}`}
          className="rounded-[14px] border border-rule-light bg-surface"
        >
          <button
            type="button"
            data-testid="order-list-row"
            onClick={() => onSelectOrder(order)}
            className="flex w-full cursor-pointer items-center gap-3.5 rounded-[14px] p-3.5 text-left transition-colors hover:bg-paper focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          >
            <ProductThumb
              order={order}
              className="h-[52px] w-[52px] flex-shrink-0 rounded-[10px] text-lg"
            />
            <div className="min-w-0 flex-1">
              <b className="block truncate text-[14px] font-semibold text-ink">
                {order.product_name || 'Unknown product'}
              </b>
              <small className="text-[12px] text-ink-muted">
                {formatOrderDate(order)}
              </small>
              {order.shipment_status && (
                <span className="ml-2 inline-block rounded-full bg-primary-pale px-2 py-0.5 text-[11px] font-medium text-primary">
                  {order.shipment_status}
                </span>
              )}
            </div>
            {(order.product_price ?? order.order_total) && (
              <div className="flex-shrink-0 font-display text-[15px] font-bold text-ink">
                {order.product_price ?? order.order_total}
              </div>
            )}
          </button>
        </OrderItem>
      ))}
      {Array.from({ length: trailingSkeletonCount }).map((_, index) => (
        <OrderListRowSkeleton key={`trailing-skeleton-${index}`} />
      ))}
    </div>
  );
}

export function OrderListSkeleton({ count = 5 }: { count?: number }) {
  return (
    <div className="space-y-2.5" data-testid="order-list-skeleton">
      {Array.from({ length: count }).map((_, index) => (
        <OrderListRowSkeleton key={`skeleton-${index}`} />
      ))}
    </div>
  );
}

function OrderListRowSkeleton() {
  return (
    <div className="flex animate-pulse items-center gap-3.5 rounded-[14px] border border-rule-light bg-surface p-3.5">
      <div className="h-[52px] w-[52px] flex-shrink-0 rounded-[10px] bg-rule" />
      <div className="min-w-0 flex-1 space-y-2">
        <div className="h-3 w-[60%] rounded bg-rule" />
        <div className="h-2.5 w-[25%] rounded bg-rule" />
      </div>
      <div className="h-3.5 w-16 flex-shrink-0 rounded bg-rule" />
    </div>
  );
}
